/**
 * QuotaManager Container Component
 *
 * Main container for quota visualization and management.
 * Displays overview dashboard, usage table, and provides quota reset functionality.
 */

import React, { useState, useCallback, useEffect } from 'react';
import { clsx } from 'clsx';
import { QuotaOverview } from './QuotaOverview';
import { QuotaUsageTable } from './QuotaUsageTable';
import { QuotaResetDialog } from './QuotaResetDialog';
import { llmAdminApi, type QuotaUsageResponseNew, type ModelHealthResponse } from '../../../services/llmAdminApi';

export interface QuotaManagerProps {
  autoRefresh?: boolean;
  refreshInterval?: number;
  token?: string;
  className?: string;
}

export const QuotaManager: React.FC<QuotaManagerProps> = ({
  autoRefresh = true,
  refreshInterval = 30000,
  token,
  className = '',
}) => {
  const [quotaData, setQuotaData] = useState<QuotaUsageResponseNew | null>(null);
  const [healthData, setHealthData] = useState<ModelHealthResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [selectedResetItem, setSelectedResetItem] = useState<{
    provider_name: string;
    pool_index: number;
    model_use?: string;
    quota_type?: string;
  } | null>(null);
  const [flushing, setFlushing] = useState(false);

  const fetchData = useCallback(async () => {
    setError(null);
    try {
      const [quotaRes, healthRes] = await Promise.all([
        llmAdminApi.getQuotaUsageNew(token),
        llmAdminApi.getModelHealth(token),
      ]);

      setQuotaData(quotaRes);
      setHealthData(healthRes);
      setLastUpdate(new Date());
    } catch (err) {
      setError(err instanceof Error ? err.message : '加载配额数据失败');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(fetchData, refreshInterval);
    return () => clearInterval(interval);
  }, [autoRefresh, refreshInterval, fetchData]);

  const handleResetQuota = useCallback(async (
    provider_name: string,
    pool_index: number,
    model_use?: string,
    quota_type?: string
  ) => {
    setSelectedResetItem({
      provider_name,
      pool_index,
      model_use,
      quota_type,
    });
    setResetDialogOpen(true);
  }, []);

  const handleConfirmReset = useCallback(async () => {
    if (!selectedResetItem) return;

    try {
      const result = await llmAdminApi.resetQuotaUsage(token, {
        provider_name: selectedResetItem.provider_name,
        pool_index: selectedResetItem.pool_index,
        model_use: selectedResetItem.model_use || '',
        quota_type: selectedResetItem.quota_type,
      });

      if (result.success) {
        await fetchData();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '重置配额失败');
    } finally {
      setResetDialogOpen(false);
      setSelectedResetItem(null);
    }
  }, [selectedResetItem, token, fetchData]);

  const handleFlushToDb = useCallback(async () => {
    setFlushing(true);
    try {
      const result = await llmAdminApi.flushQuotaToDb(token);
      if (result.success) {
        setError(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '持久化失败');
    } finally {
      setFlushing(false);
    }
  }, [token]);

  if (loading) {
    return (
      <div className={clsx('p-8 text-center text-gray-500', className)}>
        <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4" />
        加载配额数据中...
      </div>
    );
  }

  return (
    <div className={clsx('space-y-6', className)}>
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-800">配额管理</h3>
        <div className="flex items-center gap-4">
          {lastUpdate && (
            <span className="text-xs text-gray-500">
              更新于 {lastUpdate.toLocaleTimeString()}
            </span>
          )}
          <button
            type="button"
            onClick={fetchData}
            disabled={loading}
            className="text-sm text-blue-600 hover:text-blue-800 disabled:opacity-50"
          >
            刷新
          </button>
          <button
            type="button"
            onClick={handleFlushToDb}
            disabled={flushing}
            className="text-sm px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
          >
            {flushing ? '持久化中...' : '持久化到数据库'}
          </button>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center justify-between">
          <span>{error}</span>
          <button
            onClick={() => setError(null)}
            className="text-red-500 hover:text-red-700"
          >
            关闭
          </button>
        </div>
      )}

      {quotaData && healthData && (
        <>
          <QuotaOverview
            quotaData={quotaData}
            healthData={healthData}
            onReset={handleResetQuota}
          />

          <QuotaUsageTable
            quotaData={quotaData}
            healthData={healthData}
            onReset={handleResetQuota}
          />
        </>
      )}

      <QuotaResetDialog
        isOpen={resetDialogOpen}
        onClose={() => {
          setResetDialogOpen(false);
          setSelectedResetItem(null);
        }}
        onConfirm={handleConfirmReset}
        resetItem={selectedResetItem}
      />
    </div>
  );
};

export default QuotaManager;